import { useEffect, type RefObject } from "react";

/**
 * Trap Tab / Shift+Tab inside a container while ``open`` is true.
 *
 * Also restores focus to the previously-focused element when the trap
 * is released (modal closes). Safe to call on SSR — guards on
 * ``document`` access.
 *
 * Usage:
 *   const ref = useRef<HTMLDivElement>(null);
 *   useFocusTrap(ref, open);
 *   return <div ref={ref} role="dialog" ... />;
 *
 * Consumers should still set ``aria-modal``, ``aria-labelledby`` and
 * an appropriate tabIndex themselves — this hook handles only the
 * keyboard focus cycling.
 */
const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export function useFocusTrap(
  containerRef: RefObject<HTMLElement>,
  open: boolean,
): void {
  useEffect(() => {
    if (!open) return;
    if (typeof document === "undefined") return;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Tab") return;
      const root = containerRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const activeEl = document.activeElement as HTMLElement | null;
      if (e.shiftKey && activeEl === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    // Auto-focus the root so keyboard users land inside the trap.
    queueMicrotask(() => containerRef.current?.focus());
    return () => {
      document.removeEventListener("keydown", onKey);
      try {
        previouslyFocused?.focus?.();
      } catch {
        // previouslyFocused may no longer be in the DOM
      }
    };
  }, [containerRef, open]);
}
